import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import { memo, useCallback, useEffect, useMemo } from "react";
import { Store } from "../types/store";

// Constants moved outside component to prevent recreation
const STYLES = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box" as const,
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
} as const;

const STATUS_OPTIONS = [
  { value: 1, label: "有効" },
  { value: 0, label: "無効" },
];

// Define proper types for form fields
interface BaseFormField {
  label: string;
  fieldName: string;
  disabled: boolean;
}

interface FormFieldWithRules extends BaseFormField {
  rules?: any;
}

interface FormFieldWithType extends FormFieldWithRules {
  type?: string;
}

interface FormFieldWithOptions extends FormFieldWithType {
  options?: Array<{ value: number; label: string }>;
}

type FormField = FormFieldWithOptions;

const STORE_FORM_FIELDS: FormField[] = [
  { label: "店舗コード", fieldName: "store_cd", disabled: true },
  { label: "店舗名", fieldName: "store_name", disabled: true },
  { label: "得意先コード", fieldName: "client_cd", disabled: true },
  { label: "企業コード", fieldName: "company_cd", disabled: true },
  { label: "企業名", fieldName: "company_name", disabled: true },
  { label: "郵便番号", fieldName: "postal_cd", disabled: true },
  { label: "住所", fieldName: "address", disabled: true },
  { label: "電話番号", fieldName: "tel_no", disabled: true },
  { label: "FAX番号", fieldName: "fax_no", disabled: true },
  // { label: "区分4", fieldName: "kbn4", disabled: true },
  // { label: "区分5", fieldName: "kbn5", disabled: true },
  { label: "管轄物流コード", fieldName: "managed_logistics_cd", disabled: true },
  {
    label: "店舗名称",
    fieldName: "mst_store_store_name",
    disabled: false,
    rules: {
      required: MESSAGE.EMPTY,
      maxLength: {
        message: MESSAGE.MAX_LENGTH(100),
        value: 100,
      },
    },
  },
  {
    label: "有効 / 無効",
    fieldName: "mst_store_status_flag",
    disabled: false,
    type: "select",
    options: STATUS_OPTIONS,
  },
];

interface StoreFormProps {
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: Store) => void;
  isLoading: boolean;
  storeDetail: Store;
}

// Memoized header component
interface ModalHeaderProps {
  storeDetail: Store;
  onClose: () => void;
}

const ModalHeader = memo<ModalHeaderProps>(({ storeDetail, onClose }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
    <Typography
      variant="h4"
      component="h2"
      sx={{
        fontWeight: "fontWeightMedium",
        color: "var(--gray-700)",
        fontSize: { xs: 28, md: 33 },
      }}
    >
      店舗詳細
      <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
      <Typography
        component="span"
        sx={{
          fontSize: { xs: 16, md: 21 },
          fontWeight: "fontWeightMedium",
          color: "var(--gray-700)",
          ml: { xs: 0, md: 3 },
        }}
      >
        最終連携日 {formatDateTime(storeDetail.updated_at)}
      </Typography>
    </Typography>
    <CancelButton onClose={onClose} />
  </Box>
));

ModalHeader.displayName = "ModalHeader";

// Memoized footer component
interface ModalFooterProps {
  isLoading: boolean;
  hasErrors: boolean;
  storeDetail: Store;
  onClose: () => void;
}

const ModalFooter = memo<ModalFooterProps>(({ isLoading, hasErrors, storeDetail, onClose }) => (
  <>
    <Box display="flex" flexDirection="column" rowGap="15px" marginTop={4}>
      <ButtonComponent
        disabled={isLoading || hasErrors}
        type="submit"
        label="更新"
        theme="dark"
        sx={{ height: "50px", minWidth: "185px", width: "28%" }}
      />
      <Typography sx={{ fontSize: { xs: 16, md: 21 } }} fontWeight="medium" color="var(--gray-600)">
        更新日 {formatDateTime(storeDetail.mst_store_updated_at ?? "")}
      </Typography>
    </Box>
    <CancelButton sx={{ mt: 3 }} onClose={onClose} />
  </>
));

ModalFooter.displayName = "ModalFooter";

/**
 * StoreFormModal Component
 *
 * This component renders a modal form for updating client details.
 * It uses `react-hook-form` for form management and validation.
 * @param {StoreFormProps} props - The props for the component.
 * @param {boolean} props.visible - Controls the visibility of the modal.
 * @param {() => void} props.onClose - Callback function to close the modal.
 * @param {(form: Store) => void} props.onHandleSubmit - Callback function to handle form submission.
 * @param {boolean} props.isLoading - Indicates whether the form is in a loading state.
 * @param {Store} props.storeDetail - Store details for pre-filling the form fields.
 */
function StoreFormModal({
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  storeDetail,
}: StoreFormProps) {
  // Memoized default values with proper type conversion
  const itemDefaultValues = useMemo(
    () => ({ ...storeDetail }) as unknown as Partial<Store>,
    [storeDetail],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Store>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: itemDefaultValues,
  });

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: Store) => {
      if (isLoading || Object.keys(errors).length > 0) return;
      onHandleSubmit(data);
    },
    [isLoading, errors, onHandleSubmit],
  );

  // Memoized error state
  const hasErrors = Object.keys(errors).length > 0;

  // Memoized modal styles
  const modalSx = useMemo(
    () => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    [],
  );

  const boxSx = useMemo(
    () => ({
      maxHeight: "90vh",
      overflow: "auto",
      width: "80%",
      maxWidth: "870px",
      backgroundColor: "var(--white)",
      padding: { xs: 3, md: 4, xl: "30px 70px" },
      borderRadius: "21px",
      display: "flex",
      flexDirection: "column",
      rowGap: "20px",
    }),
    [],
  );

  // Effect to reset form when storeDetail changes
  useEffect(() => {
    if (storeDetail && storeDetail.store_cd) {
      reset(itemDefaultValues);
    }
  }, [storeDetail.store_cd, itemDefaultValues, reset]);

  // Memoized form fields to prevent recreation
  const formFields = useMemo(
    () =>
      STORE_FORM_FIELDS.map((field) => (
        <FormRow
          key={field.fieldName}
          sxLabel={{ pl: 4, width: "40%" }}
          label={field.label}
          sxBoxField={{ width: "60%", flexGrow: 1 }}
          sxField={{ ...STYLES.field }}
          name={field.fieldName as Path<Store>}
          type={field.type as never}
          control={control}
          rules={field.rules ? field.rules : undefined}
          options={field.type === "select" ? field.options : undefined}
          disabled={field.disabled}
        />
      )),
    [control],
  );

  return (
    <Modal sx={modalSx} open={visible} onClose={onClose}>
      <Box sx={boxSx}>
        <ModalHeader storeDetail={storeDetail} onClose={onClose} />
        <form onSubmit={handleSubmit(onSubmit)}>
          {formFields}
          <ModalFooter
            isLoading={isLoading}
            hasErrors={hasErrors}
            storeDetail={storeDetail}
            onClose={onClose}
          />
        </form>
      </Box>
    </Modal>
  );
}

export default memo(StoreFormModal);
